import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, CheckCircle, ArrowRight } from 'lucide-react';
import './Contact.css';

// ══════════════════════════════════════════
//  EmailJS Configuration
//  Replace these values with yours from emailjs.com
// ══════════════════════════════════════════
const EMAILJS_SERVICE_ID = 'service_ac3tkb5';       // e.g. 'service_xxxxxxx'

const EMAILJS_REPLY_TEMPLATE_ID = 'template_jl461ph';     // e.g. 'template_xxxxxxx'
const EMAILJS_CONTACT_TEMPLATE_ID = 'template_fqu1kgk';     // e.g. 'template_xxxxxxx'

const EMAILJS_PUBLIC_KEY  = 'ThRsVrpB2Q00cRKXl';       // e.g. 'xxxxxxxxxxxxxxx'
// ══════════════════════════════════════════

const CONTACT_INFO = [
  {
    icon: <Phone size={22} />,
    label: 'Phone',
    value: '+91 9212552928',
    link: 'tel:+919212552928',
  },
  {
    icon: <Mail size={22} />,
    label: 'Email',
    value: 'contact@shivkms.com',
    link: 'mailto:contact@shivkms.com',
  },
  {
    icon: <MapPin size={22} />,
    label: 'Address',
    value: '1000/67, Trinagar, Delhi – 110035, India',
    link: 'https://maps.google.com/?q=Trinagar+Delhi',
  },
  {
    icon: <Clock size={22} />,
    label: 'Business Hours',
    value: 'Mon – Sat: 9:00 AM – 7:00 PM IST',
    link: null,
  },
];

const INQUIRY_TYPES = [
  'Product Demo Request',
  'Sales Inquiry',
  'Technical Support',
  'Partnership',
  'Career Opportunity',
  'General Inquiry',
];

export default function Contact() {
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    from_name: '',
    from_email: '',
    phone: '',
    company: '',
    inquiry_type: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.from_name || !form.from_email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_REPLY_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_CONTACT_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
      toast.success('Message sent! We\'ll respond within 24 hours.');
      setForm({ from_name: '', from_email: '', phone: '', company: '', inquiry_type: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to send. Please email us directly at contact@shivkms.com');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-bg" />
        <div className="container">
          <div className="contact-hero-inner">
            <span className="eyebrow">Get In Touch</span>
            <h1>Let's Build Something <span className="gradient-text">Remarkable Together</span></h1>
            <p>Have a project in mind? We'd love to hear about it. Talk to our team and get a free consultation.</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-py">
        <div className="container">
          <div className="contact-layout">
            {/* Left: Info */}
            <div className="contact-info-col">
              <div className="contact-info-card">
                <h3 className="cic-title">Contact Information</h3>
                <p className="cic-sub">Reach out through any channel and our team will respond promptly.</p>
                <div className="cic-items">
                  {CONTACT_INFO.map((item, i) => (
                    <div key={i} className="cic-item">
                      <div className="cic-icon">{item.icon}</div>
                      <div className="cic-text">
                        <span className="cic-label">{item.label}</span>
                        {item.link ? (
                          <a href={item.link} className="cic-value cic-link" target={item.link.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer">
                            {item.value}
                          </a>
                        ) : (
                          <span className="cic-value">{item.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map embed placeholder */}
              <div className="contact-map">
                <iframe
                  title="SHIVKMS Location"
                  width="100%"
                  height="220"
                  style={{ border: 0, borderRadius: '14px', filter: 'invert(90%) hue-rotate(180deg) brightness(0.85)' }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.5985707395183!2d77.13!3d28.69!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDQxJzI0LjAiTiA3N8KwMDcnNDguMCJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
                />
              </div>

              {/* Quick Links */}
              <div className="quick-links">
                <h4 className="ql-title">Quick Actions</h4>
                <a href="tel:+919212552928" className="ql-btn">
                  <Phone size={16} /> Call Us Now
                </a>
                <a href="https://wa.me/919212552928" target="_blank" rel="noopener noreferrer" className="ql-btn ql-btn-green">
                  <MessageSquare size={16} /> WhatsApp Chat
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="contact-form-col">
              {sent ? (
                <div className="contact-success">
                  <div className="success-icon">
                    <CheckCircle size={48} />
                  </div>
                  <h3>Message Received!</h3>
                  <p>Thank you for reaching out to SHIVKMS. Our team will get back to you within 24 business hours.</p>
                  <button className="btn-outline" onClick={() => setSent(false)}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="contact-form" noValidate>
                  <h3 className="cf-title">Send us a Message</h3>
                  <p className="cf-sub">Fill in the details below and we'll get back to you within 24 hours.</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        className="input-field"
                        type="text"
                        name="from_name"
                        placeholder="Your full name"
                        value={form.from_name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address *</label>
                      <input
                        className="input-field"
                        type="email"
                        name="from_email"
                        placeholder="you@company.com"
                        value={form.from_email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        className="input-field"
                        type="tel"
                        name="phone"
                        placeholder="+91 XXXXX XXXXX"
                        value={form.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Company / Organization</label>
                      <input
                        className="input-field"
                        type="text"
                        name="company"
                        placeholder="Your company name"
                        value={form.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Inquiry Type</label>
                    <select
                      className="input-field select-field"
                      name="inquiry_type"
                      value={form.inquiry_type}
                      onChange={handleChange}
                    >
                      <option value="">Select inquiry type…</option>
                      {INQUIRY_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Subject *</label>
                    <input
                      className="input-field"
                      type="text"
                      name="subject"
                      placeholder="What can we help you with?"
                      value={form.subject}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea
                      className="input-field"
                      name="message"
                      placeholder="Describe your project, requirements, or questions…"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary cf-submit"
                    disabled={sending}
                  >
                    {sending ? (
                      <>Sending… <span className="spinner" /></>
                    ) : (
                      <>Send Message <Send size={18} /></>
                    )}
                  </button>

                  <p className="cf-privacy">
                    By submitting this form, you agree to our privacy policy. We never share your information.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-py faq-section">
        <div className="faq-bg" />
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">FAQs</span>
            <h2>Common <span className="gradient-text">Questions</span></h2>
            <div className="divider" />
          </div>
          <div className="faq-grid">
            {[
              { q: 'How long does implementation take?', a: 'Depending on the product, most deployments go live within 4–12 weeks, including data migration, customization, and staff training.' },
              { q: 'Do you offer cloud hosting?', a: 'Yes — all SHIVKMS products are available as cloud-hosted SaaS or on-premises installations depending on your data governance requirements.' },
              { q: 'Can I customize the software for my workflows?', a: 'Absolutely. Our modular architecture lets us adapt workflows, reports, and UI without building from scratch. Most customizations ship within 2–4 weeks.' },
              { q: 'What support do you provide post-launch?', a: 'We provide 24/7 technical support, quarterly business reviews, and a dedicated account manager for enterprise clients.' },
              { q: 'Is there a free trial or demo available?', a: 'Yes — reach out to us through this form or by phone and we\'ll set up a live demo tailored to your industry and use case.' },
              { q: 'Do you integrate with third-party software?', a: 'Yes. Our RESTful API platform integrates with most ERP, CRM, depository, and payment systems. Custom integrations are available on request.' },
            ].map((faq, i) => (
              <div key={i} className="faq-card card">
                <h4 className="faq-q">{faq.q}</h4>
                <p className="faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
